import { useEffect, useState } from "react";

import { Mail, Clock, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useEmailPreferences } from "@/hooks/useEmailPreferences";
import { supabase } from "@/integrations/supabase/client";

interface EmailCampaign {
  id: string;
  campaign_type: string;
  sent_at: string;
  email_data: any;
}

const campaignTypeLabels: Record<string, { label: string; icon: string }> = {
  no_project_24h: { label: "Project Reminder", icon: "📋" },
  no_session_12h: { label: "Session Reminder", icon: "💬" },
  waitlist_welcome: { label: "Waitlist Welcome", icon: "👋" },
  email_verified: { label: "Email Verified", icon: "✅" },
  account_approved: { label: "Account Approved", icon: "🎉" },
  takeout_ready: { label: "Data Takeout", icon: "📦" },
  daily_digest: { label: "Daily Digest", icon: "📊" },
  weekly_recommendations: { label: "Weekly Recommendations", icon: "💡" },
  verification_reminder: { label: "Verification Reminder", icon: "⏰" },
};

const getEmailTypeCategory = (campaignType: string): string => {
  if (campaignType.includes("project") || campaignType.includes("session") || campaignType === "waitlist_welcome") {
    return "onboarding_emails";
  }
  if (campaignType.includes("digest")) {
    return "digest_emails";
  }
  if (campaignType.includes("recommendations")) {
    return "content_recommendations";
  }
  if (campaignType.includes("verified") || campaignType.includes("approved") || campaignType.includes("reminder")) {
    return "admin_emails";
  }
  if (campaignType === "takeout_ready") {
    return "onboarding_emails";
  }
  return "marketing_emails";
};

export default function NotificationHistory() {
  const { user } = useAuth();
  const { preferences, loading: prefsLoading } = useEmailPreferences();
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from("email_campaigns")
          .select("*")
          .eq("user_id", user.id)
          .order("sent_at", { ascending: false });

        if (error) throw error;
        setCampaigns(data || []);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [user]);

  const wouldReceiveEmail = (campaignType: string): boolean => {
    const category = getEmailTypeCategory(campaignType);
    const prefValue = preferences[category as keyof typeof preferences];
    return prefValue !== false;
  };

  if (loading || prefsLoading) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Notification History</h1>
        <p className="text-muted-foreground">
          View all emails sent to your account and see how your current preferences affect them
        </p>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          The status icons show whether you would receive each email type based on your{" "}
          <a href="/profile" className="underline font-medium">
            current email preferences
          </a>
          .
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {campaigns.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Mail className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No email history yet</p>
              <p className="text-sm text-muted-foreground">
                Emails sent to your account will appear here
              </p>
            </CardContent>
          </Card>
        ) : (
          campaigns.map((campaign) => {
            const typeInfo = campaignTypeLabels[campaign.campaign_type] || {
              label: campaign.campaign_type,
              icon: "📧",
            };
            const wouldReceive = wouldReceiveEmail(campaign.campaign_type);

            return (
              <Card key={campaign.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">{typeInfo.icon}</div>
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {typeInfo.label}
                          {wouldReceive ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Clock className="h-3 w-3" />
                          {new Date(campaign.sent_at).toLocaleString()}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant={wouldReceive ? "default" : "secondary"}>
                      {wouldReceive ? "Would Receive" : "Would Block"}
                    </Badge>
                  </div>
                </CardHeader>

                {campaign.email_data && Object.keys(campaign.email_data).length > 0 && (
                  <>
                    <Separator />
                    <CardContent className="pt-4">
                      <div className="text-sm">
                        <p className="font-medium mb-2">Email Details:</p>
                        <div className="bg-muted p-3 rounded-md space-y-1">
                          {Object.entries(campaign.email_data).map(([key, value]) => (
                            <div key={key} className="flex gap-2">
                              <span className="text-muted-foreground capitalize">
                                {key.replace(/_/g, " ")}:
                              </span>
                              <span className="font-medium">
                                {typeof value === "object" ? JSON.stringify(value) : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
